/*eslint no-restricted-globals: 0*/
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { getConfig } from 'tw-oi-core'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { filterTopicsByField, sortItemsByTitle } from 'tw-oi-core/utils/data'
import {
  trackSearchResultsTopicClick,
  trackSearchKeyword,
  trackEmptyResultsSearchKeyword,
  trackPublicationClick,
  trackRecentlyViewedTopicClick,
} from 'tw-oi-core/services/analytics'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import { ROUTE, MESSAGE, BRAND_NISSAN, BRAND_INFINITI } from '../config'

import * as ContentsActions from 'tw-oi-core/actions/ContentsActions'
import ScreenHead from '../components/ScreenHead'
import ScreenContainer from '../components/ScreenContainer'
import TopicsList from '../components/TopicsList'
import Loader from '../components/Loader'
import SearchInput from '../components/SearchInput'
import ErrorMessage from '../components/ErrorMessage'
import BodyClass from '../components/BodyClass'
import TopicsListSearch from '../components/TopicsListSearch'
import { parseLocationPreviewParams } from '../utils/publication'

import '../styles/Topics.scss'
import InfinitiTopicsList from '../components/InfinitiTopicsList/InfinitiTopicsList'

class Search extends Component {
  static propTypes = {
    fetching: PropTypes.bool,
    errorSearch: PropTypes.object,
    topics: ImmutablePropTypes.list,
    publications: ImmutablePropTypes.list,
    recent: ImmutablePropTypes.list,
    currentYear: PropTypes.string,
    currentModel: PropTypes.string,
    currentBrand: PropTypes.string,
    ContentsActions: PropTypes.shape({
      setSearchQuery: PropTypes.func.isRequired,
      getContents: PropTypes.func.isRequired,
    }).isRequired,
    match: PropTypes.object.isRequired,
    baseRoute: PropTypes.string.isRequired,
    searchQuery: PropTypes.string,
    searchTopics: ImmutablePropTypes.list,
    searchContentsUpdated: PropTypes.string,
    isDesktop: PropTypes.bool,
    isLandscape: PropTypes.bool,
    history: PropTypes.shape({
      location: PropTypes.shape({
        search: PropTypes.string
      })
    }),
  }

  constructor(props) {
    super(props)

    this.state = {
      sortDescend: false,
    }
  }

  shouldComponentUpdate(nextProps) {
    // do not hide loader until new content will arrive
    return !(
      !nextProps.errorSearch &&
      !nextProps.fetching &&
      this.props.fetching &&
      nextProps.searchContentsUpdated === this.props.searchContentsUpdated
    )
  }

  componentDidUpdate(prevProps) {
    if (prevProps.searchQuery !== this.props.searchQuery) {
      if (this.props.searchQuery) {
        this.getSearchContents(this.props.searchQuery)
      }
    }

    if (prevProps.searchContentsUpdated !== this.props.searchContentsUpdated) {
      this.trackAnalyticsEvents()
    }
  }

  getSearchContents = searchQuery => {
    const { DEFAULT_LOCALE } = getConfig()
    const { currentBrand, currentYear, currentModel, ContentsActions } = this.props

    ContentsActions.getContents(currentBrand, currentYear, currentModel, DEFAULT_LOCALE, searchQuery)
  }

  /**
   * Captures analytics search events
   */
  trackAnalyticsEvents() {
    const { searchQuery, searchTopics } = this.props

    if (!searchQuery) {
      return
    }

    // track empty search results and return early
    if (searchTopics && searchTopics.size === 0) {
      trackEmptyResultsSearchKeyword(searchQuery)
    }

    // track search keyword
    trackSearchKeyword(searchQuery)
  }

  /**
   * Triggers search analytics event when search results are clicked
   *
   * @param {Immutable.Map} topic
   */
  onTopicClick(topic) {
    switch (topic.type) {
      case 'topic':
        trackSearchResultsTopicClick(topic.title)
        break
      case 'publication':
        trackPublicationClick(topic.title)
        break
      default:
      // Do nothing in case of unsupported content type
    }
  }

  renderTopics = () => {
    const { topics, recent, searchQuery, errorSearch, fetching, baseRoute, currentBrand, history } = this.props
    const location = history?.location
    const queryParams = parseLocationPreviewParams(location)

    if (!searchQuery) {
      const recentlyViewed = sortItemsByTitle(
        filterTopicsByField(topics, recent ? recent.toJS() : [], 'resourceKey', true)
      )

      if (currentBrand === BRAND_INFINITI) {
        return (
          <InfinitiTopicsList
            topics={recentlyViewed}
            onClick={topic => trackRecentlyViewedTopicClick(topic.get('title'))}
            emptyText={MESSAGE.EMPTY_RECENT}
            baseRoute={baseRoute + ROUTE.SEARCH}
          />
        )
      }

      return (
        <TopicsList
          topics={recentlyViewed}
          onClick={topic => trackRecentlyViewedTopicClick(topic.get('title'))}
          emptyText={MESSAGE.EMPTY_RECENT}
          baseRoute={baseRoute + ROUTE.SEARCH}
          isPreview={queryParams?.preview}
        />
      )
    }

    if (errorSearch) {
      return (
        <ErrorMessage className='inverse inline' retryAction={() => this.getSearchContents(this.props.searchQuery)} />
      )
    }

    if (searchQuery) {
      if (fetching) {
        return <Loader className='inverse' />
      }

      return (
        <TopicsListSearch
          searchQuery={searchQuery}
          baseRoute={baseRoute + ROUTE.SEARCH}
          onClick={topic => this.onTopicClick(topic)}
          isPreview={queryParams?.preview}
        />
      )
    }
  }

  render() {
    const {
      currentYear,
      currentBrand,
      currentModel,
      match,
      searchQuery,
      ContentsActions,
      errorSearch,
      fetching,
      isDesktop,
      isLandscape,
    } = this.props

    return (
      <div className='Topics one'>
        <ScreenHead landscapeOverlay={false}>
          <em>
            {currentYear} {currentBrand}
          </em>{' '}
          {currentModel}
        </ScreenHead>
        <div className='screen-content'>
          <ScreenContainer currentUrl={match.url}>
            <div className='topics'>
              <BodyClass className='collapsed-search' condition={isLandscape} />
              <SearchInput searchQuery={searchQuery} onChange={ContentsActions.setSearchQuery} autoFocus={isDesktop} />
              {(!searchQuery || (!fetching && !errorSearch)) && (
                <div className='topics-subtitle'>
                  {searchQuery ? 'Search Results' : 'Recently Viewed'}
                  {currentBrand === BRAND_NISSAN ? <hr /> : null}
                </div>
              )}

              {this.renderTopics()}
            </div>
          </ScreenContainer>
        </div>
      </div>
    )
  }
}

function mapStateToProps({
  contents,
  vehicle,
  user: {
    recent,
    media: { isLandscape, isDesktop },
  },
}) {
  const {
    topics,
    fetching,
    currentAsset,
    fetchingAsset,
    searchQuery,
    searchTopics,
    errorSearch,
    searchContentsUpdated,
    publications,
  } = contents
  const { currentYear, currentModel, currentBrand } = vehicle

  recent = recent.getIn([currentYear, currentModel])

  return {
    topics,
    recent,
    fetching,
    currentAsset,
    fetchingAsset,
    currentYear,
    currentModel,
    currentBrand,
    searchQuery,
    searchTopics,
    searchContentsUpdated,
    errorSearch,
    isDesktop,
    isLandscape,
    publications,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    ContentsActions: bindActionCreators(ContentsActions, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Search)
